import { apiAxios } from "./apiConfig";

export interface UserGroup {
  name: string;
}

export interface UserUpdater {
  id: string;
  name: string;
}
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  studyId: string;
  isDeleted: boolean;
  totalLessonsInWeek: string;
  completedLessons: string;
  lastActivity: string | null;
  progress: string | null | number;
  group: UserGroup | null;
  week: any;
  statusUpdatedDate: string | null;
  updater: UserUpdater;
}

export interface UserListResponse {
  status: boolean;
  data: {
    count: number;
    finalData: User[];
    activeCount: string;
    inactiveCount: string;
    deletedCount: string;
  };
}

export const getUserList = async ({
  pageId,
  limit,
  status,
}: {
  pageId: number;
  limit: number;
  status: "active" | "inactive" | "delete";
}): Promise<UserListResponse> => {
  const response = await apiAxios.get<UserListResponse>(
    `/user/list?pageId=${pageId}&limit=${limit}&status=${status}`,
  );

  return response.data;
};

/* =====================================================
   GET USER DETAILS (WEEKLY REPORT)
===================================================== */

export interface LessonUserStatus {
  lessonId: string;
  lessonName: string;
  lessonUserStatus: boolean;
}

export interface ActivityData {
  activityId: string;
  activityName: string;
  activityDescription: string;
  activityLogoName: string;
  goal: number;
  goalUnit: string;
  isActive: boolean;
  activityOption: string[];
  activityValues: string | number | string[] | null;
  activitySortOrder: number;
  activityMappingId: string;
}

export interface UserWeeklyDetail {
  enrolledDate: string;
  firstName: string;
  email: string;
  lastName: string;
  mobileNumber: string;
  studyId: string;
}

export interface WeeklyReportData {
  receivedDate: string;
  reportDetailId: string;
  status: string;
  weekId: string;
  weekName: string;
  weekReportMappingId: string;
}

export interface WeeklyUserReport {
  weekId: string;
  weekName: string;
  lessonDetails: LessonUserStatus[];
  totalLesson: string;
  completedLesson: string;
  weeklyReport: WeeklyReportData[];
  activityData: ActivityData[];
  totalWeeks: {
    weekId: string;
    weekName: string;
    weekSort: number;
  }[];
  userDetails: UserWeeklyDetail[];
  streakValue: string | number;
}

export interface GetUserDetailsResponse {
  status: boolean;
  data: WeeklyUserReport[];
}

export const getUserDetailsApi = async ({
  userId,
  weekId,
  startDate,
  endDate,
}: {
  userId: string;
  weekId?: string;
  startDate?: string;
  endDate?: string;
}): Promise<GetUserDetailsResponse> => {
  const params = new URLSearchParams();

  if (weekId) params.append("weekId", weekId);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);

  const response = await apiAxios.get<GetUserDetailsResponse>(
    `/user/${userId}?${params.toString()}`,
  );

  return response.data;
};

/* =====================================================
   GET USER FEEDBACK (PAGINATION)
===================================================== */

export interface FeedbackCreator {
  id: string;
  name: string;
}

export interface UserFeedback {
  id: string;
  feedback: string;
  isActive: boolean;
  userId: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  user_id?: string; // backend added this field
  creator: FeedbackCreator | null;
  updater: FeedbackCreator | null;
}

export interface UserFeedbackList {
  count: number;
  rows: UserFeedback[];
}

export interface GetUserFeedbackResponse {
  status: boolean;
  data: UserFeedbackList;
}

export const getUserFeedbackApi = async (
  userId: string,
  pageId: number,
  limit: number,
): Promise<GetUserFeedbackResponse> => {
  const response = await apiAxios.get<GetUserFeedbackResponse>(
    `/user/${userId}/feedback?pageId=${pageId}&limit=${limit}`,
  );

  return response.data;
};

/* =====================================================
   CREATE USER FEEDBACK
===================================================== */

export interface CreateUserFeedbackPayload {
  feedback: string;
}

export interface CreateUserFeedbackResponse {
  status: boolean;
  data: {
    id: string;
    isActive: boolean;
    feedback: string;
    userId: string;
    createdBy: string;
    updatedAt: string;
    createdAt: string;
    updatedBy: string | null;
  };
}

export const createUserFeedbackApi = async (
  userId: string,
  payload: CreateUserFeedbackPayload,
): Promise<CreateUserFeedbackResponse> => {
  const response = await apiAxios.post<CreateUserFeedbackResponse>(
    `/user/${userId}/feedback`,
    payload,
  );

  return response.data;
};

/* =====================================================
   GET CMS WEEK REPORT DETAILS
===================================================== */

export interface CmsReportOption {
  optionId: string;
  optionName: string;
  optionSortOrder: number;
}

export interface CmsReportQuestion {
  questionId: string;
  questionName: string;
  questionDesc: string;
  questionSortOrder: number;
  answer: string | null;
  options: CmsReportOption[];
}

export interface CmsReportCategory {
  id: string;
  name: string;
  sortOrder: number;
  questions: CmsReportQuestion[];
}

export interface CmsReportData {
  id: string;
  weekReportMappingId: string;
  reportDetails: CmsReportQuestion[] | CmsReportCategory[];
  createdAt: string;
  updatedAt: string;
  reports: {
    weekId: string;
    weeks: {
      name: string;
    };
  };
}

export interface GetCmsReportResponse {
  status: boolean;
  data: CmsReportData;
}

export const getCmsReportApi = async (
  weekReportMappingId: string,
): Promise<GetCmsReportResponse> => {
  const response = await apiAxios.get<GetCmsReportResponse>(
    `/report/${weekReportMappingId}`,
  );

  return response.data;
};

/* =====================================================
   UPDATE USER DETAILS
===================================================== */

export interface UpdateUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

export interface UpdateUserResponse {
  status: boolean;
  data: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
    updatedAt: string;
  };
}

export const updateUserApi = async (
  userId: string,
  payload: UpdateUserPayload,
): Promise<UpdateUserResponse> => {
  const response = await apiAxios.put<UpdateUserResponse>(
    `/user/${userId}`,
    payload,
  );

  return response.data;
};

// Search Name

export interface SearchUser {
  id: string;
  firstName: string;
  lastName: string;
  studyId?: string;
  isActive: boolean;
  email: string;
  mobileNumber: string;
}

export interface SearchUserResponse {
  status: boolean;
  data: SearchUser[];
}

export const searchUserApi = async (
  name: string,
): Promise<SearchUserResponse> => {
  const response = await apiAxios.get<SearchUserResponse>(
    `/user/search?name=${encodeURIComponent(name)}`,
  );

  return response.data;
};

export const exportUserReportApi = async ({
  startDate,
  endDate,
  userId,
}: {
  startDate: string;
  endDate: string;
  userId: string;
}): Promise<Blob> => {
  const response = await apiAxios.get<Blob>("/report/users/export", {
    params: { startDate, endDate, userId },
    responseType: "blob",
  });

  return response.data;
};
